import { Grid } from '@mui/material';
import type { Identifier, XYCoord } from 'dnd-core';
import React from 'react';
import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { DraggableExportFieldIdentifier } from '../../components/Export/ExportConfigurationForm';

export interface AppDraggableItemProps {
  id: any;
  index: number;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  content: JSX.Element;
}

interface DragItem {
  index: number;
  id: string;
}

export class DraggableItemData {
  id!: number;
  key!: string;

  static forExportConfiguration<R>(items: { field: string; label: string }[]): DraggableExportFieldIdentifier<R>[] {
    return items.map((item, index) => {
      return {
        index,
        id: index,
        label: item.label,
        value: item.label,
        field: item.field as R,
        key: `${item.field}${index}`,
      };
    }) as DraggableExportFieldIdentifier<R>[];
  }

  static toMap<T>(items: { field: T; value: string; index: number }[]): Map<T, { value: string; index: number }> {
    return new Map(items.map((item, idx) => [item.field, { value: item.value, index: idx }]));
  }
}

const style = {
  border: '1px dashed gray',
  padding: '0.5rem 1rem',
  marginBottom: '.5rem',
  backgroundColor: 'white',
  cursor: 'move',
};

const itemType = 'AppDraggableItem';

export default function AppDraggableItem({ id, index, moveItem, content }: AppDraggableItemProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: itemType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveItem(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: itemType,
    item: () => {
      return { id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));
  return (
    <div ref={ref} data-handler-id={handlerId} style={{ opacity }}>
      <Grid container columnGap={2} display='flex' style={style} alignItems={'center'}>
        <Grid item flexGrow={1} display='flex'>
          {content}
        </Grid>
      </Grid>
    </div>
  );
}
